import { Capacitor } from '@capacitor/core';
// Consumer apps can enable different providers. Webpack replaces environment
// keys that exist during an app build, but an optional missing key may remain as
// `process.env.KEY` and throw in the browser where `process` does not exist.
function readEnv(read) {
    try {
        return read();
    }
    catch (_a) {
        return undefined;
    }
}
const env = {
    googleClientId: readEnv(() => process.env.GOOGLE_CLIENT_ID),
    googleIosClientId: readEnv(() => process.env.GOOGLE_CLIENT_ID_IOS),
    siteRoot: readEnv(() => process.env.SITE_ROOT),
    appleClientId: readEnv(() => process.env.APPLE_SIGN_IN_CLIENT_ID),
    appleRedirectUri: readEnv(() => process.env.APPLE_SIGN_IN_REDIRECT_URI),
    facebookClientId: readEnv(() => process.env.FACEBOOK_CLIENT_ID),
    facebookClientToken: readEnv(() => process.env.FACEBOOK_CLIENT_TOKEN),
};
let pluginModulePromise;
export class SocialLoginError extends Error {
    constructor(kind, message, cause) {
        super(message);
        this.kind = kind;
        this.cause = cause;
        this.name = 'SocialLoginError';
    }
}
export function resolveGoogleRedirectUri(siteRoot, platform) {
    if (platform !== 'web')
        return undefined;
    const root = siteRoot === null || siteRoot === void 0 ? void 0 : siteRoot.trim();
    if (!root) {
        throw new SocialLoginError('configuration', 'SITE_ROOT is required for Google web login');
    }
    try {
        return new URL('/signin', root).toString();
    }
    catch (error) {
        throw new SocialLoginError('configuration', 'SITE_ROOT is not a valid URL', error);
    }
}
/** Converts plugin and browser failures into the small error set used by UI. */
export function classifySocialLoginError(error) {
    if (error instanceof SocialLoginError)
        return error;
    const message = error instanceof Error ? error.message : String(error);
    if (/cancel(?:led|ed|lation)?/i.test(message)) {
        return new SocialLoginError('cancelled', 'Social login was cancelled', error);
    }
    if (/network|fetch|offline/i.test(message)) {
        return new SocialLoginError('network', 'Social login network error', error);
    }
    return new SocialLoginError('provider', 'Social login provider error', error);
}
/** Allows buttons to keep user cancellation silent while reporting real errors. */
/** Loads the plugin lazily so server-side imports do not initialize Capacitor. */
async function loadPluginModule() {
    if (!pluginModulePromise) {
        // Cache the module, not `SocialLogin` itself. The Capacitor proxy exposes a
        // `then` property that Promise resolution mistakes for an async method.
        pluginModulePromise = import('@capgo/capacitor-social-login').catch((error) => {
            pluginModulePromise = undefined;
            throw error;
        });
    }
    return pluginModulePromise;
}
let initializationPromise;
/** Initializes only the providers configured by the consuming application. */
export function initializeSocialLogin() {
    if (!initializationPromise) {
        initializationPromise = loadPluginModule()
            .then(({ SocialLogin }) => {
            const platform = Capacitor.getPlatform();
            const options = {};
            if (env.googleClientId) {
                options.google = {
                    webClientId: env.googleClientId,
                    iOSClientId: env.googleIosClientId,
                    iOSServerClientId: env.googleClientId,
                    redirectUrl: resolveGoogleRedirectUri(env.siteRoot, platform),
                    mode: 'online',
                };
            }
            if (platform === 'ios') {
                options.apple = {};
            }
            else if (platform === 'web' &&
                env.appleClientId &&
                env.appleRedirectUri) {
                options.apple = {
                    clientId: env.appleClientId,
                    redirectUrl: env.appleRedirectUri,
                };
            }
            if (env.facebookClientId) {
                options.facebook = {
                    appId: env.facebookClientId,
                    clientToken: env.facebookClientToken,
                };
            }
            return SocialLogin.initialize(options);
        })
            .catch((error) => {
            // Allow a later sign-in attempt to retry transient import/SDK failures.
            initializationPromise = undefined;
            throw classifySocialLoginError(error);
        });
    }
    return initializationPromise;
}
/**
 * Resumes Capgo's web popup callback when the current `/signin` window contains
 * a pending OAuth response. Normal page loads and native platforms return false.
 */
export async function resumePendingSocialLoginRedirect() {
    var _a, _b, _c, _d, _e, _f;
    if (typeof globalThis.window === 'undefined')
        return false;
    let pendingOAuthState;
    try {
        pendingOAuthState =
            (_b = (_a = globalThis.localStorage) === null || _a === void 0 ? void 0 : _a.getItem('social_login_oauth_pending')) !== null && _b !== void 0 ? _b : null;
    }
    catch (_g) {
        return false;
    }
    const callbackUrl = `${(_d = (_c = globalThis.location) === null || _c === void 0 ? void 0 : _c.search) !== null && _d !== void 0 ? _d : ''}&${(_f = (_e = globalThis.location) === null || _e === void 0 ? void 0 : _e.hash) !== null && _f !== void 0 ? _f : ''}`;
    const hasResponse = /(?:^|[?#&])(code|access_token|id_token|error)=/i.test(callbackUrl);
    const hasContext = /(?:^|[?#&])(state|iss)=/i.test(callbackUrl);
    if (Capacitor.getPlatform() !== 'web' ||
        pendingOAuthState === null ||
        !globalThis.window.opener ||
        !hasResponse ||
        !hasContext) {
        return false;
    }
    // The returned popup must initialize Capgo so it can pass the OAuth result
    // back to the window that opened it.
    await initializeSocialLogin();
    return true;
}
/** Apple login is available through the web SDK and native iOS only. */
export function isAppleLoginSupported() {
    return ['ios', 'web'].includes(Capacitor.getPlatform());
}
/** Creates the one-time value used to bind the Apple response to this request. */
export function createAppleNonce() {
    // The same nonce is sent to Apple and later verified by lincd-auth.
    const bytes = new Uint8Array(32);
    globalThis.crypto.getRandomValues(bytes);
    const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
    const encoded = typeof globalThis.btoa === 'function'
        ? globalThis.btoa(binary)
        : Buffer.from(bytes).toString('base64');
    return encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
/** Opens Google sign-in and returns a backend-ready ID-token payload. */
export function buildGoogleLoginOptions(platform, scopes) {
    return platform === 'android' ? {} : { scopes };
}
export async function loginWithGoogle(scopes = ['email', 'profile']) {
    if (!env.googleClientId) {
        throw new SocialLoginError('configuration', 'GOOGLE_CLIENT_ID is not configured');
    }
    await initializeSocialLogin();
    try {
        // Capgo's Android implementation rejects `scopes` unless the consuming
        // app installs a custom Activity. Basic Google identity already includes
        // the profile and email required by lincd-auth, so native Android can use
        // the plugin defaults while web and iOS keep caller-provided scopes.
        const options = buildGoogleLoginOptions(Capacitor.getPlatform(), scopes);
        const { result } = await (await loadPluginModule()).SocialLogin.login({
            provider: 'google',
            options,
        });
        if (result.responseType !== 'online' || !result.idToken) {
            throw new Error('Google login did not return an ID token');
        }
        return {
            ...result.profile,
            // Backend verification needs the ID token; do not propagate access tokens.
            authentication: { idToken: result.idToken },
        };
    }
    catch (error) {
        throw classifySocialLoginError(error);
    }
}
/** Opens Apple sign-in on web or iOS and returns its token with the request nonce. */
export async function loginWithApple(scopes = ['email', 'name']) {
    if (!isAppleLoginSupported()) {
        throw new SocialLoginError('configuration', 'Apple login is not supported on this platform');
    }
    if (Capacitor.getPlatform() === 'web') {
        if (!env.appleClientId) {
            throw new SocialLoginError('configuration', 'APPLE_SIGN_IN_CLIENT_ID is not configured');
        }
        if (!env.appleRedirectUri) {
            throw new SocialLoginError('configuration', 'APPLE_SIGN_IN_REDIRECT_URI is not configured');
        }
    }
    await initializeSocialLogin();
    const nonce = createAppleNonce();
    try {
        const { result } = await (await loadPluginModule()).SocialLogin.login({
            provider: 'apple',
            options: { scopes, nonce },
        });
        if (!result.idToken) {
            throw new Error('Apple login did not return an ID token');
        }
        return {
            email: result.profile.email,
            givenName: result.profile.givenName,
            familyName: result.profile.familyName,
            identityToken: result.idToken,
            authorizationCode: result.authorizationCode,
            nonce,
        };
    }
    catch (error) {
        throw classifySocialLoginError(error);
    }
}
/** Opens Facebook sign-in and returns the token required for server validation. */
export async function loginWithFacebook(permissions = ['email', 'public_profile']) {
    var _a;
    if (!env.facebookClientId) {
        throw new SocialLoginError('configuration', 'FACEBOOK_CLIENT_ID is not configured');
    }
    await initializeSocialLogin();
    try {
        const { result } = await (await loadPluginModule()).SocialLogin.login({
            provider: 'facebook',
            options: { permissions, limitedLogin: false },
        });
        if (!((_a = result.accessToken) === null || _a === void 0 ? void 0 : _a.token)) {
            throw new Error('Facebook login did not return an access token');
        }
        // The backend resolves the trusted Facebook profile from this token.
        return { accessToken: result.accessToken.token };
    }
    catch (error) {
        throw classifySocialLoginError(error);
    }
}
//# sourceMappingURL=SocialLoginClient.js.map